import { useContext, useState, useMemo, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { BoxFull, Button, InputText } from "iuvity-ui-components";
import { Tag } from "iuvity-ui-components/dist/tag";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/index";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Column } from "primereact/column";
import { FormSkeleton } from "../../components/skeleton/FormSkeleton";
import { FilterMatchMode } from "primereact/api";
import { CustomDialog, DialogProps } from "../../components/dialog/CustomDialog";
import { UploadNewFiles } from "./UploadNewFiles";
import { useUploadAndDownload } from "../../hooks/useUploadAndDownload";
import { STATUS_PROCESS_COLOR_TAG } from "../../constants";
import { downloadTemplate } from "../../services/configurations/pointsOfAttention/uploadanddownload";
import { capitalizeFirstLetter, reportDate } from "../../utils";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

const formBlankValues = {
  id: '',
  user: '',
  date: '',
  registrationNumber: '',
  latitude: '',
  stateProcess: '',
  drafted: '',
};

export const UploadAndDownloadPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { language, selectedApp, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const { control, setValue, reset } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "id",
          header: t("PointsOfAttention.idLabel"),
        },
        {
          field: "user",
          header: t("PointsOfAttention.userLabel"),
        },
        {
          field: "date",
          header: t("PointsOfAttention.dateLabel"),
        },
        {
          field: "registrationNumber",
          header: t("PointsOfAttention.registrationNumberLabel"),
        },
        // {
        //   field: "latitude",
        //   header: t("PointsOfAttention.latitudLabel"),
        // },
        {
          field: "stateProcess",
          header: t("PointsOfAttention.stateProcessLabel"),
        },
        {
          field: "drafted",
          header: t("PointsOfAttention.draftedLabel"),
        },
        {
          field: "actions",
          header: t("PointsOfAttention.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const { dataFiles, reloadFnUploadAndDownload, uploadAndDownloadAction } = useUploadAndDownload("DATA");
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [administrationFilters, setAdministrationFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    user: { value: null, matchMode: FilterMatchMode.CONTAINS },
    date: { value: null, matchMode: FilterMatchMode.CONTAINS },
    registrationNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    latitude: { value: null, matchMode: FilterMatchMode.CONTAINS },
    stateProcess: { value: null, matchMode: FilterMatchMode.CONTAINS },
    drafted: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
  const [auxModal, auxModalController] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const tenantname = capitalizeFirstLetter(selectedApp?.tenantname?.split(" ").join(""));
  const appname = capitalizeFirstLetter(selectedApp?.appname);
  const env = selectedApp?.env;

  const onDownloadTemplateAction = async () => {
    const date = new Date();
    const report = reportDate(date);
    const csvdata = await downloadTemplate();
    let blob = new Blob([csvdata], { type: 'text/csv' });
    let filename = `${tenantname}_${appname}_${env}_Puntos_${report}.csv`;
    let downloadLink = document.createElement("a");
    downloadLink.download = filename;
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
    downloadLink.click();
  };

  const onDownloadAction = (file: any) => {
    if(file !== null) {
      const link = document.createElement('a');
      link.href = file || "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const onAction = async (data: any) => {
    const actionResult = await uploadAndDownloadAction("UPDATE", data)
    if (actionResult === "SUCCESS") {
      reloadFnUploadAndDownload();
    }
  };

  const handleModalProps = async (data: any) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: t("PointsOfAttention.cancelProcessModalTitle"),
      content: `${t("PointsOfAttention.cancelProcessModalContent")}`,
      onConfirm: () => onAction(data),
    }));
  };

  const onClearFilters = () => {
    setAdministrationFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      id: { value: null, matchMode: FilterMatchMode.CONTAINS },
      user: { value: null, matchMode: FilterMatchMode.CONTAINS },
      date: { value: null, matchMode: FilterMatchMode.CONTAINS },
      registrationNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
      latitude: { value: null, matchMode: FilterMatchMode.CONTAINS },
      stateProcess: { value: null, matchMode: FilterMatchMode.CONTAINS },
      drafted: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    reset(formBlankValues);
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`PointsOfAttention.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`PointsOfAttention.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`PointsOfAttention.${options.field}Placeholder`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  }

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("ValidationMessages.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFilters}
      />
    )
  }
  
  const filterApplyTemplate = (options: any) => {
      return (
        <Button
          label={t("ValidationMessages.applyFilterLabel")}
          className="p-button"
          onClick={options.filterApplyCallback}
        />
      )
  }
  
  const withColumn = (field: string) => {
    if(field === "id") return "1.5rem"
    if(field === "registrationNumber") return "12rem"
    if(field === "stateProcess") return "15rem"
    return "10rem"
  }

  return (
    <>
      <div className="points__tabdescription">
        <span>{t("PointsOfAttention.manage_filesDescription")}</span>
      </div>
      <BoxFull className="ml-0 mt-0 mx-0 px-0" variant="tertiary">
        <div className="tablefilter-mc2">
          <Button
            className="hover"
            label={t("Tenants.clearFilterBtnLabel")}
            variant="p-button"
            icon="iu iu-icon-error"
            onClick={onClearFilters}
          />
          <SubheaderActions
            className="multiactions-c my-0 mx-0 py-0 px-0"
            actions={subheaderActionsPrivilegeValidator([
              {
                id: "uploadNewFiles",
                icon: "iu iu-icon-upload",
                label: t("PointsOfAttention.uploadNewFilesBtnLabel"),
                onClick: () => setImportModalVisible(true),
                style: {
                  display: (!permissiondInfoByRole.CREATE && !permissiondInfoByRole.UPDATE) ? 'none' : undefined,
                },
                disabled: !dataFiles,
              },
              {
                id: "downloadTemplate",
                icon: "iu iu-icon-download",
                label: t("PointsOfAttention.downloadTemplateBtnLabel"),
                onClick: () => onDownloadTemplateAction(),
                style: {
                  width: "10rem !important",
                  display: (!permissiondInfoByRole.READ && !permissiondInfoByRole.CREATE && !permissiondInfoByRole.UPDATE) ? 'none' : undefined,
                },
                disabled: !dataFiles,
              },
              {
                id: "downloadGeneral",
                icon: "iu iu-icon-download",
                label: t("PointsOfAttention.downloadGeneralBtnLabel"),
                onClick: () => navigate('/pointsofattention/downloadgeneral'),
                style: {
                  width: "10rem !important",
                  display: (!permissiondInfoByRole.READ && !permissiondInfoByRole.CREATE && !permissiondInfoByRole.UPDATE) ? 'none' : undefined,
                },
                disabled: !dataFiles,
              },
            ])}
          />
        </div>
      </BoxFull>
        <BoxFull className="dt-c px-0 py-0 mx-0" variant="tertiary">
          {dataFiles ? (
            <DataTable
            header={
              <TableHeader
              title={t("PointsOfAttention.manage_filesTabTitle")}
              columns={columns}
              selectedColumns={selectedColumns}
              toggleColumns={setSelectedColumns}
              filters={administrationFilters}
              setFilters={setAdministrationFilters}
              reload={reloadFnUploadAndDownload}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            filters={administrationFilters}
            sortField="id"
            dataKey="id"
            sortOrder={1}
            value={dataFiles?.map((configDataSelected: any) => ({
              ...configDataSelected,
              stateProcess: <Tag 
                value={t(`PointsOfAttention.state${configDataSelected?.stateProcess}`)}
                severity={STATUS_PROCESS_COLOR_TAG[configDataSelected?.stateProcess]}
                style={{ borderRadius: "0.5rem"}}
              />,
              drafted: t(`PointsOfAttention.resetRecord${configDataSelected?.drafted}`),
              actions: (
                <>
                  <Button
                    icon="iu"
                    className="p-button-rounded p-button-info p-button-outlined act-btn"
                    aria-label="Delete"
                    tooltip={t("PointsOfAttention.urlOriginLabel")}
                    tooltipOptions={{ position: "bottom" }}
                    variant="p-button"
                    onClick={() => onDownloadAction(configDataSelected?.urlOrigin)}
                    style={{
                      marginRight: "1rem",
                      color: "var(--infoColor)",
                    }}
                  >
                    <i className="iu iu-icon-download act-btn" />
                  </Button>
                  <Button
                    icon="iu"
                    className="p-button-rounded p-button-success p-button-outlined act-btn"
                    aria-label="Delete"
                    tooltip={t("PointsOfAttention.urlTransformedLabel")}
                    tooltipOptions={{ position: "bottom" }}
                    variant="p-button"
                    onClick={() => onDownloadAction(configDataSelected?.urlTransformed)}
                    style={{
                      marginRight: "1rem",
                      color: !configDataSelected?.urlTransformed
                      ? "var(--successColorTint60)" 
                      : "var(--successColor)",
                    }}
                    disabled={!configDataSelected?.urlTransformed}
                  >
                    <i className="iu iu-icon-down act-btn" />
                  </Button>
                  <Button
                    icon="iu"
                    className="p-button-rounded p-button-danger p-button-outlined act-btn"
                    aria-label="Delete"
                    tooltip={t("PointsOfAttention.cancelBtnLabel")}
                    tooltipOptions={{ position: "bottom" }}
                    variant="p-button"
                    onClick={() => handleModalProps(configDataSelected)}
                    style={{
                      color: 
                        (
                          configDataSelected?.stateProcess === "TRANSFORMING" ||
                          configDataSelected?.stateProcess === "VALIDATING" ||
                          configDataSelected?.stateProcess === "GENERATING_FILE" ||
                          configDataSelected?.stateProcess === "LOADING_FILE"
                        ) ? "var(--errorColor)" : "var(--errorColorTint60)"
                    }}
                    disabled={
                      (
                        configDataSelected?.stateProcess === "TRANSFORMING" ||
                        configDataSelected?.stateProcess === "VALIDATING" ||
                        configDataSelected?.stateProcess === "GENERATING_FILE" ||
                        configDataSelected?.stateProcess === "LOADING_FILE"
                      ) ? false : true
                    }
                  >
                    <i className="iu iu-icon-close act-btn" />
                  </Button>
                </>
                ),
              }))}
              scrollable
              resizableColumns
              globalFilterFields={[
                "id",
                "user",
                "date",
                "registrationNumber",
                "latitude",
                "stateProcess",
                "drafted",
              ]}
              filterDisplay="menu"
              >
              {selectedColumns.map((col: any) => {
                return (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable={!(col.field === "actions")}
                    align={col.field === "actions"  ? "center" : "left"}
                    headerStyle={
                      col.field === "actions" 
                      ? { justifyContent: "center" }
                      : undefined
                    }
                    style={{
                      boxShadow:
                      col.field === "actions"
                      ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                      : undefined,
                      maxWidth: col.field === "actions" ? "132px" : undefined,
                      whiteSpace: "normal",
                      minWidth: withColumn(col.field),
                    }}
                    frozen={col.field === "actions" ? true : false}
                    alignFrozen={col.field === "actions" ? "right" : undefined}
                    filterField={col.field}
                    showFilterMatchModes={false}
                    filterMenuStyle={{ width: "23rem" }}
                    filter={col.field === "actions" ? false : true}
                    filterElement={filterTemplate}
                    filterApply={filterApplyTemplate}
                    filterClear={filterClearTemplate}
                  />
                );
              })}
            </DataTable>
          ) : (
            <FormSkeleton length={8} />
            )}
        </BoxFull>

      {importModalVisible && (
        <UploadNewFiles
          setVisible={setImportModalVisible}
          reloadFn={reloadFnUploadAndDownload}
          auxModalController={auxModalController}
        />
      )}
      <CustomDialog
        title={auxModal.title}
        content={auxModal.content}
        visible={auxModal.visible}
        handleModalState={auxModalController}
        onConfirm={auxModal.onConfirm}
      />
    </>
  );
};
